import React from "react";
import { classNames } from "src/helpers/classNames";
import { previousDateInString } from "src/helpers/dateHelper";
import {
  getCustomAppStatusBadgeStyles,
  getCustomAppTypeBadgeStyles,
  getAppInitials,
  getAppGradient,
} from "src/helpers/customAppHelper";
import { ClockIcon, GlobeAltIcon } from "@heroicons/react/24/outline";

const CustomAppItem = ({ data = null }) => {
  const { className: statusClass, label: statusLabel, dotClass } =
    getCustomAppStatusBadgeStyles(data?.status);
  const { className: typeClass, label: typeLabel } =
    getCustomAppTypeBadgeStyles(data?.appType);

  const initials = getAppInitials(data?.name);
  const gradient = getAppGradient(data?.name);

  const domainDisplay = data?.publicUrl
    ? data.publicUrl.replace(/^https?:\/\//, "")
    : data?.hostname || "No domain assigned";

  return (
    <div className="flex w-full flex-col gap-y-2.5 text-left">
      {/* Top Row: Avatar + Title + Status */}
      <div className="flex w-full items-start justify-between gap-x-2.5">
        {/* Left: Avatar & App info */}
        <div className="flex items-center gap-x-3 min-w-0 flex-1">
          <div
            className={classNames(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br font-bold text-xs shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105",
              gradient
            )}
          >
            {data?.appType === "frontend" ? (
              <GlobeAltIcon className="h-5 w-5 text-white/95" />
            ) : (
              <span className="tracking-wider">{initials}</span>
            )}
          </div>

          <div className="min-w-0 flex-1 overflow-hidden">
            <h4
              className="truncate text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors"
              title={data?.name}
            >
              {data?.name || "Untitled Application"}
            </h4>
            <p
              className="truncate text-xs text-gray-500 font-mono text-[11px] mt-0.5"
              title={domainDisplay}
            >
              {domainDisplay}
            </p>
          </div>
        </div>

        {/* Right: Status Pill */}
        <div className="flex-shrink-0 pt-0.5">
          <div
            className={classNames(
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-all",
              statusClass
            )}
          >
            <span className={classNames("h-1.5 w-1.5 rounded-full", dotClass)} />
            <span>{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* Bottom Row: Timestamp & App Type Pill */}
      <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-[11px] text-gray-400">
        <span className="inline-flex items-center gap-1 text-gray-500 font-medium">
          <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
          <span>{previousDateInString(data?.createdAt)}</span>
        </span>

        {data?.appType && (
          <span
            className={classNames(
              "rounded-md border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold",
              typeClass
            )}
          >
            {typeLabel}
          </span>
        )}
      </div>
    </div>
  );
};

export default CustomAppItem;
